import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner, faCheckCircle, faExclamationTriangle,
  faChurch, faEnvelopeOpenText
} from '@fortawesome/free-solid-svg-icons';
import './NewsletterSignupPage.css';

const getApiBaseUrl = () => {
  if (process.env.REACT_APP_USE_EMULATORS === 'true') {
    return 'http://127.0.0.1:5001/churchaware-8f158/us-central1';
  }
  // Production: use hosting rewrites (relative paths)
  return '';
};

const API_BASE_URL = getApiBaseUrl();

function NewsletterSignupPage() {
  const { orgSlug } = useParams();

  const [status, setStatus] = useState('loading'); // loading, ready, submitting, success, error
  const [orgName, setOrgName] = useState('');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const fetchOrgInfo = async () => {
      try {
        const orgInfoPath = API_BASE_URL
          ? `${API_BASE_URL}/newsletterOrgInfo?slug=${encodeURIComponent(orgSlug)}`
          : `/api/newsletter/org-info?slug=${encodeURIComponent(orgSlug)}`;
        const response = await fetch(orgInfoPath);
        const data = await response.json();

        if (response.ok) {
          setOrgName(data.orgName);
          setStatus('ready');
        } else {
          setErrorMessage(data.error || 'Organization not found.');
          setStatus('error');
        }
      } catch (err) {
        console.error('Error fetching org info:', err);
        setErrorMessage('Unable to load organization info. Please try again.');
        setStatus('error');
      }
    };

    if (orgSlug) {
      fetchOrgInfo();
    } else {
      setErrorMessage('Invalid newsletter link.');
      setStatus('error');
    }
  }, [orgSlug]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    if (!email.trim()) {
      setErrorMessage('Email address is required.');
      return;
    }

    setStatus('submitting');

    try {
      const subscribePath = API_BASE_URL
        ? `${API_BASE_URL}/newsletterSubscribe`
        : '/api/newsletter/subscribe';
      const response = await fetch(subscribePath, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          orgSlug,
          email: email.trim(),
          firstName: firstName.trim() || undefined,
          lastName: lastName.trim() || undefined,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setStatus('success');
      } else {
        setErrorMessage(data.error || 'Something went wrong. Please try again.');
        setStatus('ready');
      }
    } catch (err) {
      console.error('Error subscribing:', err);
      setErrorMessage('Failed to subscribe. Please check your connection and try again.');
      setStatus('ready');
    }
  };

  if (status === 'loading') {
    return (
      <div className="newsletter-signup-page">
        <div className="newsletter-loading">
          <FontAwesomeIcon icon={faSpinner} spin size="2x" />
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (status === 'error' && !orgName) {
    return (
      <div className="newsletter-signup-page">
        <div className="newsletter-error-state">
          <FontAwesomeIcon icon={faExclamationTriangle} size="3x" />
          <h2>Newsletter Not Available</h2>
          <p>{errorMessage}</p>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="newsletter-signup-page">
        <div className="newsletter-header">
          <FontAwesomeIcon icon={faChurch} className="newsletter-church-icon" />
          <h1>{orgName}</h1>
        </div>
        <div className="newsletter-success-state">
          <FontAwesomeIcon icon={faCheckCircle} size="3x" className="newsletter-success-icon" />
          <h2>Check Your Email</h2>
          <p>We've sent a confirmation link to <strong>{email}</strong>. Click the link to confirm your subscription and start receiving the weekly newsletter.</p>
          <p className="newsletter-success-note">If you don't see it, check your spam folder.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="newsletter-signup-page">
      <div className="newsletter-header">
        <FontAwesomeIcon icon={faChurch} className="newsletter-church-icon" />
        <h1>{orgName}</h1>
        <p>Weekly Newsletter</p>
      </div>

      <div className="newsletter-form-container">
        <div className="newsletter-form-intro">
          <FontAwesomeIcon icon={faEnvelopeOpenText} className="newsletter-intro-icon" />
          <p>Subscribe to receive a weekly email with upcoming events and news from {orgName}.</p>
        </div>

        <form onSubmit={handleSubmit}>
          {errorMessage && (
            <div className="newsletter-form-error">
              <FontAwesomeIcon icon={faExclamationTriangle} />
              {errorMessage}
            </div>
          )}

          <div className="newsletter-form-group">
            <label htmlFor="newsletter-email">Email Address *</label>
            <input
              id="newsletter-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
              disabled={status === 'submitting'}
            />
          </div>

          <div className="newsletter-form-group">
            <label htmlFor="newsletter-first-name">First Name (optional)</label>
            <input
              id="newsletter-first-name"
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="First name"
              disabled={status === 'submitting'}
            />
          </div>

          <div className="newsletter-form-group">
            <label htmlFor="newsletter-last-name">Last Name (optional)</label>
            <input
              id="newsletter-last-name"
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Last name"
              disabled={status === 'submitting'}
            />
          </div>

          <button
            type="submit"
            className="newsletter-submit-btn"
            disabled={status === 'submitting'}
          >
            {status === 'submitting' ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin />
                Subscribing...
              </>
            ) : (
              'Subscribe'
            )}
          </button>
        </form>

        <p className="newsletter-privacy-note">
          You can unsubscribe at any time using the link in every newsletter email.
        </p>
      </div>
    </div>
  );
}

export default NewsletterSignupPage;
